var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Page } from "./domtools";
import { CobrowserError, CobrowserErrors } from "./errors";
import { API_COMMAND, COBROWSER_PERMISSION, FilterType, MQTT_EVENTS, } from "./model";
import { Player } from "./player";
import { Users } from "./users";
import { log, wait } from "./utils";
import { UPDATE_PERIOD } from "./options";
import { Gzip } from "./domtools/compression/native";
import { Zstd } from "./domtools/compression/zstd";
var SenderState;
(function (SenderState) {
    SenderState[SenderState["INITIALIZING"] = 0] = "INITIALIZING";
    SenderState[SenderState["INITIALIZED"] = 1] = "INITIALIZED";
    SenderState[SenderState["TERMINATED"] = 2] = "TERMINATED";
})(SenderState || (SenderState = {}));
export class Sender {
    constructor(auviousClient, config, listeners = {}) {
        this.auviousClient = auviousClient;
        this.config = config;
        this.listeners = listeners;
        this.options = {
            scroll: {
                enabled: false,
            },
        };
        this.sessionId = null;
        this.users = new Users();
        this.version = 0;
        this.state = SenderState.INITIALIZING;
        /** next flush is lite */
        this.flushLite = false;
        // page might not be initiated
        this.filterBuffer = [];
        /** timeout id failsafe for notifying client of termination  */
        this.terminationCountdown = -1;
        this.checkCompress = { zstd: false, gzip: false };
        this.compressionSupport = {
            zstd: {
                compress: false,
                decompress: false,
            },
            gzip: {
                compress: false,
                decompress: false,
            },
        };
        this.api = auviousClient.apiResourceFactory("cobrowser/api");
        this.commonSubscription = this.auviousClient.events.setEventHandlers({
            onMessageReceived: (event) => {
                var _a;
                if (!event.payload.type.startsWith("Cobrowsing")) {
                    return;
                }
                this.setVersion(event.payload.version);
                switch (event.payload.type) {
                    case MQTT_EVENTS.CREATED: {
                        this.onSessionCreated(event.payload);
                        break;
                    }
                    case MQTT_EVENTS.VIEW_REQUESTED: {
                        this.viewRequested(event.payload);
                        break;
                    }
                    case MQTT_EVENTS.CONTROL_REQUESTED: {
                        this.controlRequested(event.payload);
                        break;
                    }
                    case MQTT_EVENTS.PAGE_UPDATED: {
                        this.remotePageUpdates(event.payload);
                        break;
                    }
                    case MQTT_EVENTS.OPTION_UPDATED: {
                        this.optionUpdated(event.payload);
                        break;
                    }
                    case MQTT_EVENTS.RECONNECTED: {
                        this.users.addUser({
                            id: event.payload.userId,
                            endpoint: event.payload.userEndpointId,
                        });
                        (_a = this.capture) === null || _a === void 0 ? void 0 : _a.sync();
                        break;
                    }
                    case MQTT_EVENTS.TERMINATED: {
                        this.terminated(event.payload);
                        break;
                    }
                }
            },
        });
    }
    /**
     * reconnect to a previous session
     * @param sessionId the cobrowser session to reconnect to
     */
    reconnect(sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            var _c, _d;
            if (this.capture) {
                return;
            }
            this.sessionId = sessionId;
            const response = yield this.api.create(this.createCommand(API_COMMAND.RECONNECT), {
                urlPostfix: `session/reconnect`,
            });
            // @ts-expect-error
            if (response.error) {
                // @ts-expect-error
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.options = response.options;
            for (const id in response.remotes) {
                (_c = this.checkCompress).zstd || (_c.zstd = !!((_a = response.remotes[id].compressionSupport.zstd) === null || _a === void 0 ? void 0 : _a.decompress));
                (_d = this.checkCompress).gzip || (_d.gzip = !!((_b = response.remotes[id].compressionSupport.gzip) === null || _b === void 0 ? void 0 : _b.decompress));
                this.users.addUser({ id, endpoint: response.remotes[id].remoteEndpointId }, response.remotes[id].permissions);
            }
            yield this.checkCompression();
            this.state = SenderState.INITIALIZED;
            yield this.createPage();
        });
    }
    /**
     * @deprecated use `reconnect` instead
     *
     * reconnect to a previous session
     * @param cobrowserSessiondId the cobrowser session to reconnect to
     */
    resume(cobrowserSessiondId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.capture) {
                return;
            }
            this.sessionId = cobrowserSessiondId;
            const response = yield this.api.create(this.createCommand(API_COMMAND.RESUME), {
                urlPostfix: `session/resume`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.options = response.options;
            for (const id in response.remotes) {
                this.users.addUser({ id, endpoint: response.remotes[id].remoteEndpointId }, response.remotes[id].permissions);
            }
            this.state = SenderState.INITIALIZED;
            yield this.createPage();
            this.sendEvent("resumed", {
                sessionId: this.sessionId,
                users: JSON.parse(this.users.toString()),
            });
        });
    }
    /**
     * grant page access to remote user, view or control
     * @param access View or control
     * @param user The user to grant access to
     */
    allowAccess(access, user) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const isView = access === COBROWSER_PERMISSION.VIEW;
            if (this.users.hasPermission(user.userId, access)) {
                return;
            }
            yield this.checkCompression();
            let response;
            try {
                response = yield this.api.create(this.createCommand(isView ? API_COMMAND.VIEW_REQUEST : API_COMMAND.CONTROL_REQUEST, {
                    targetId: user.userId,
                    targetEndpointId: user.userEndpointId,
                    compressionSupport: this.compressionSupport,
                }), {
                    urlPostfix: `session/permissions/${isView ? "view" : "control"}/accept`,
                });
            }
            catch (err) {
                if (((_a = err.response) === null || _a === void 0 ? void 0 : _a.status) === 410) {
                    throw new CobrowserError(CobrowserErrors.Cancelled, "Session has been cancelled by agent.");
                }
            }
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            if (!this.users.hasId(user.userId)) {
                this.users.addUser({ id: user.userId, endpoint: user.userEndpointId });
            }
            this.users.setPermission(user.userId, access);
            if (isView) {
                this.state = SenderState.INITIALIZED;
                if (!(yield this.createPage())) {
                    this.capture.sync();
                }
            }
        });
    }
    checkCompression() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.checkCompress.gzip && Gzip.canCompress) {
                this.compressionSupport.gzip.compress = true;
            }
            else if (this.checkCompress.zstd) {
                // if it can decompress (load smaller wasm-only), it can compress
                yield Zstd.load(false, true);
                this.compressionSupport.zstd.compress =
                    Zstd.canDecompress && Zstd.usesWasm;
            }
        });
    }
    createPage() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.capture) {
                return false;
            }
            this.capture = yield Page.create({
                target: this.config.target,
                webworkerPath: this.config.webworkerPath,
                user: {
                    endpoint: this.config.userEndpointId,
                    id: this.config.userId,
                    metadata: this.config.userMetadata,
                },
                allowOrigins: this.config.allowOrigins,
                compressionSupport: this.compressionSupport,
            }, this.users, {
                mousemove: (ev) => this.sendEvent("mousemove", ev),
                flush: (ev) => this.onFlush(ev),
            });
            this.player = new Player(this.capture);
            for (const filter of this.filterBuffer) {
                this.capture.filter.filterNode(filter.elm, filter.type);
            }
            if (this.config.filters) {
                this.capture.filter.add(...this.config.filters);
            }
            this.filterBuffer = [];
            this.capture.setEvents({
                input: true,
                cursor: true,
                highlight: true,
                scroll: this.options.scroll.enabled,
            });
            this.flush();
            return true;
        });
    }
    flush() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isActive()) {
                this.capture.flush(this.flushLite);
                this.flushLite = false;
            }
        });
    }
    onFlush(data) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.isActive()) {
                return;
            }
            this.flushLite = false;
            if (data) {
                const formData = new FormData();
                formData.append("metadata", new Blob([
                    JSON.stringify(this.createCommand(API_COMMAND.PAGE_UPDATE, data.batch)),
                ], {
                    type: "application/json",
                }));
                formData.append("events", new Blob([data.events], {
                    type: typeof data.events === "string"
                        ? "text/plain;charset=UTF-8"
                        : "application/octet-stream",
                }));
                if (data.attachments.events.length) {
                    formData.append("attachments", new Blob([JSON.stringify(data.attachments.events)], {
                        type: "application/json",
                    }));
                    for (const id in data.attachments.data) {
                        formData.append(id, new Blob([data.attachments.data[id]], {
                            type: "application/octet-stream",
                        }));
                    }
                }
                for (let tries = 1; true; tries++) {
                    try {
                        // post updates
                        const response = yield this.api.create(formData, {
                            urlPostfix: "session/owner/update",
                        });
                        this.setVersion(response.version);
                        break;
                    }
                    catch (ex) {
                        yield wait(Math.min(tries, 10) * UPDATE_PERIOD);
                        // stop when terminated
                        if (!this.isActive() ||
                            (ex === null || ex === void 0 ? void 0 : ex.status) === 410 ||
                            ((_a = ex === null || ex === void 0 ? void 0 : ex.response) === null || _a === void 0 ? void 0 : _a.status) === 410) {
                            return;
                        }
                        if (tries === 20) {
                            this.flushLite = true;
                        }
                    }
                }
            }
            setTimeout(() => this.flush(), UPDATE_PERIOD);
        });
    }
    enable(option, settings) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.capture) {
                this.capture.setEvents({ scroll: true });
            }
            const response = yield this.api.create(this.createCommand(API_COMMAND.OPTION_ENABLE, {
                option,
                settings,
            }), {
                urlPostfix: `session/options/enable`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.options.scroll.enabled = true;
        });
    }
    disable(option) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.create(this.createCommand(API_COMMAND.OPTION_DISABLE, {
                option,
            }), {
                urlPostfix: `session/options/disable`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            if (option === "record") {
                if (this.capture) {
                    this.capture.setEvents({ scroll: false });
                }
                this.setVersion(response.version);
                this.options.scroll.enabled = false;
            }
        });
    }
    revokeAccess(access, user) {
        return __awaiter(this, void 0, void 0, function* () {
            const isView = access === COBROWSER_PERMISSION.VIEW;
            const response = yield this.api.create(this.createCommand(isView ? API_COMMAND.VIEW_REVOKE : API_COMMAND.CONTROL_REVOKE, {
                targetId: user.userId,
                targetEndpointId: user.userEndpointId,
            }), {
                urlPostfix: `session/permissions/${isView ? "view" : "control"}/revoke`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.users.unsetPermission(user.userId, access);
            if (isView) {
                this.users.removeUser(user.userId);
                if (!this.users.length) {
                    this.terminate();
                }
            }
        });
    }
    denyAccess(access, user) {
        return __awaiter(this, void 0, void 0, function* () {
            const isView = access === COBROWSER_PERMISSION.VIEW;
            const response = yield this.api.create(this.createCommand(isView ? API_COMMAND.VIEW_DENY : API_COMMAND.CONTROL_DENY, {
                targetId: user.userId,
                targetEndpointId: user.userEndpointId,
                permission: access,
            }), {
                urlPostfix: `session/permissions/deny`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.users.unsetPermission(user.userId, access);
            if (isView) {
                this.users.removeUser(user.userId);
            }
        });
    }
    clearTerminationTimer() {
        if (this.terminationCountdown !== -1) {
            this.auviousClient.getTimers().clearTimeout(this.terminationCountdown);
            this.terminationCountdown = -1;
        }
    }
    setTerminationTimer() {
        this.clearTerminationTimer();
        this.terminationCountdown = this.auviousClient
            .getTimers()
            .setTimeout(() => {
            this.terminationCountdown = -1;
            this.terminated();
        }, 5000);
    }
    /** end the session for all */
    terminate() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isActive() || !this.sessionId) {
                this.terminated();
                return;
            }
            // in any case, force termination
            this.setTerminationTimer();
            const response = yield this.api.create(this.createCommand(API_COMMAND.TERMINATE), {
                urlPostfix: "session/terminate",
            });
            if (response.error) {
                throw new Error(response.msg);
            }
        });
    }
    optionUpdated(ev) {
        return __awaiter(this, void 0, void 0, function* () {
            const [option, info] = Object.entries(ev.payload)[0];
            if (this.capture) {
                if (option === "scroll") {
                    if (info.enabled) {
                        this.capture.setEvents({ scroll: true });
                    }
                    else {
                        this.capture.setEvents({ scroll: false });
                    }
                }
            }
            this.options[option] = info;
            this.sendEvent("option", {
                option,
                enabled: info.enabled,
            });
        });
    }
    terminated(event) {
        if (this.state !== SenderState.TERMINATED) {
            this.state = SenderState.TERMINATED;
            this.sendEvent("terminated", null);
        }
        this.cleanup();
    }
    isActive() {
        return this.state !== SenderState.TERMINATED || this.capture;
    }
    cleanup() {
        var _a, _b;
        (_a = this.capture) === null || _a === void 0 ? void 0 : _a.cleanup();
        (_b = this.commonSubscription) === null || _b === void 0 ? void 0 : _b.dispose();
        this.clearTerminationTimer();
        this.listeners = {};
        this.capture = null;
        this.player = null;
        this.commonSubscription = null;
    }
    remotePageUpdates(ev) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.users.hasEndpoint(ev.userEndpointId) && this.isActive()) {
                this.player.buffer.append(ev.payload);
                this.player.play();
            }
            else {
                log("received page updates from unknown user", ev);
            }
        });
    }
    /**
     * filter an html element **before** being shared
     * @param elm the html element to filter
     * @param display whether to show to remote if filtered
     */
    filterElement(elm, display = FilterType.Display) {
        if (this.capture) {
            this.capture.filter.filterNode(elm, display);
        }
        else {
            this.filterBuffer.push({ elm, type: display });
        }
    }
    /**
     * add event listener, previous one gets overwritten
     */
    on(event, listener) {
        // @ts-ignore
        this.listeners[event] = listener;
    }
    /**
     * sends event back to client's listener
     */
    sendEvent(event, data) {
        if (this.listeners[event]) {
            try {
                this.listeners[event](data);
            }
            catch (error) {
                log(error);
            }
        }
    }
    onSessionCreated(event) {
        this.sessionId = event.sessionId;
        this.sendEvent("sessionCreated", {
            timestamp: event.timestamp,
        });
    }
    viewRequested(event) {
        var _a, _b;
        this.sessionId = event.sessionId;
        // if the other party accepts can decompress, check if we can compress later
        this.checkCompress.zstd =
            !!((_a = event.payload.compressionSupport.zstd) === null || _a === void 0 ? void 0 : _a.decompress);
        this.checkCompress.gzip =
            !!((_b = event.payload.compressionSupport.gzip) === null || _b === void 0 ? void 0 : _b.decompress);
        this.sendEvent("viewRequested", {
            sessionId: event.sessionId,
            remoteId: event.userId,
            remoteEndpointId: event.userEndpointId,
            timestamp: event.timestamp,
        });
    }
    controlRequested(event) {
        this.sendEvent("controlRequested", {
            sessionId: event.sessionId,
            remoteId: event.userId,
            remoteEndpointId: event.userEndpointId,
            timestamp: event.timestamp,
        });
    }
    setVersion(version) {
        if (Number.isInteger(version)) {
            this.version = version;
        }
    }
    createCommand(type, payload) {
        return {
            type,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString(),
            userId: this.config.userId,
            userEndpointId: this.config.userEndpointId,
            payload,
            version: this.version,
        };
    }
}
//# sourceMappingURL=sender.js.map